// scoring — puntaje de las respuestas del llenado de formularios.
//
// QUÉ HACE. Calcula cuántos puntos vale una respuesta y cómo se suman:
//   - getItemObtainedPoints(item, answer): puntos que obtiene un ítem según su tipo y su respuesta.
//     Devuelve el string 'na' cuando no aplica; `formVisibility.js` depende de que sea string.
//   - getItemPossiblePoints(item, answer): puntos máximos que puede dar el ítem.
//   - sumArray, sumKey, sumKeyWhereNotEqual: sumas del puntaje por grupo, del puntaje total y de los
//     reportes. Se comportan como un `reduce` sin valor inicial: con un solo elemento devuelven
//     ese elemento tal cual y con strings concatenan, y esa semántica es parte del resultado.
//   - getAnsweredItemObtainedPoints(item, answer): puntos de un ítem a partir de su respuesta guardada, con
//     las ramas de IA (`ia_result`, `ai_reference_image`).
//   - getLogicPropertyItemIds, getLogicItemsRoutingToVisibleGroup, normalizeItemObtainedPoints: lógica de
//     visibilidad; un ítem con lógica cuya respuesta rutea a un grupo visible vale 1 o 0 según tenga respuesta.
//   - getObtainedPointsByGroup, getNAPointsByGroup, getPossiblePointsByGroup: puntos por grupo.
//   - getOccurrenceScore (ocurrencia abierta) y getClosedOccurrenceScore (cerrada: el guardado): puntaje total.
//   - getSelfEvaluationResult: niveles de los tres estándares de la autoevaluación y resultado final.
//   - getTablesDifferenceSum y getExtraPropertiesAnswerSum: total de las propiedades extra de la ocurrencia.
//   - getCemexScoreResult y getMifelScoreResult: puntajes de cliente que `OccurrenceHeaderSection.vue` muestra en lugar del
//     porcentaje. Cemex: SUSPENDIDO, CONDICIONADO o APROBADO según los ítems en "no" por sufijo. Mifel: calificación
//     ponderada (70 % grupos "alto", 30 % grupos "medio") y su nivel de exposición.
//
// REGLAS DEL ARCHIVO. Son funciones puras: no leen la store, la red, `$q` ni `$t`; lo que necesitan
// llega por parámetro. No importa `utils/index.js` (sería un ciclo: `index.js` importa este archivo).
// Pueden leer los campos de los ítems que reciben, pero no escriben en ellos: la escritura de
// `obtained_points` y `possible_points` en cada ítem la hace el getter de la store.
//
// LECTURAS PEREZOSAS. Varias funciones reciben funciones y no valores ya leídos (`isLogicItem`,
// `routesToVisibleGroup`, `hasAnswer`, `getPossiblePointsByItem`): las invocan solo cuando la regla las
// necesita. Leerlas por adelantado cambia qué se escribe en los ítems y qué dependencias recalcula Vue.
// Por la misma razón `getSelfEvaluationResult` lee `item.obtained_points` solo si la prioridad coincide, y
// `getClosedOccurrenceScore` está separada de `getOccurrenceScore`.
// `getCemexScoreResult` recibe `getAnswers` y `getGroupItems` y no los valores: el componente ordena en sitio los
// ítems de cada grupo y la función lee las respuestas solo cuando la regla llega a ese punto; adelantarlo
// cambia qué se escribe y cuándo si una respuesta lanza.
//
// QUIÉN LO USA. Los getters de puntaje de `stores/occurrences/getters.js` y, vía `$utils`, los
// componentes del llenado y los reportes (`$utils.getItemObtainedPoints`, `$utils.sumKey`, …).
// `utils/index.js` publica estos nombres sin cambios, más `getCemexScoreResult` y `getMifelScoreResult`, que
// llama `OccurrenceHeaderSection.vue`.
//
// PRUEBAS. Su resultado lo fijan el spec dorado `test/occurrences/scoring-golden.spec.js` y `scoring.spec.js`.

// Librerias
import _ from 'lodash'

// Utils
import { answerToLowerText, normalizeCheckitemValue } from '@/utils/answerOptions'

/**
 * Suma los valores de un arreglo; un arreglo vacío da 0.
 * El primer elemento es el valor inicial del `reduce` y se suma el resto, igual que un `reduce` sin valor
 * inicial. No es lo mismo que empezar en 0: con un solo elemento lo devuelve tal cual (un string o un
 * `undefined` no se tocan) y con strings los concatena.
 * @param {Array<*>} values
 * @returns {*}
 */
function sumFromFirst (values) {
  const [first, ...rest] = values

  return values.length > 0 ? rest.reduce((prev, curr) => prev + curr, first) : 0
}

/**
 * Suma los números de un arreglo; un arreglo vacío da 0.
 * Con un solo elemento devuelve ese elemento tal cual y con strings los concatena (ver `sumFromFirst`).
 * @param {Array<number>} arr
 * @returns {number}
 */
export function sumArray (arr) {
  return sumFromFirst(arr)
}

/**
 * Suma el campo `key` de cada elemento de `collection`; una colección vacía da 0.
 * Misma suma que `sumArray`.
 * @param {Array<Object>} collection
 * @param {string} key
 * @returns {number}
 */
export function sumKey (collection, key) {
  return sumFromFirst(collection.map(el => el[key]))
}

/**
 * Como `sumKey`, pero ignora los elementos cuyo campo vale `value` (por ejemplo 'na', que no cuenta).
 * @param {Array<Object>} collection
 * @param {string} key
 * @param {*} value Valor del campo que se excluye de la suma.
 * @returns {number}
 */
export function sumKeyWhereNotEqual (collection, key, value) {
  return sumFromFirst(collection.map(el => el[key]).filter(el => el !== value))
}

/**
 * Puntos que obtiene un ítem con la respuesta dada, según su tipo:
 * - label o respuesta "na" ('na', 'NA', '"na"', '"NA"'): el string 'na', que no cuenta en el puntaje.
 *   `formVisibility.js` depende de que sea string y no un número.
 * - upload-edit-picture sin respuesta: 0.
 * - form-table: solo puntúa con `options.scored_table`; toma el total de la columna `total_for_score`
 *   (con tope `item.value`, mínimo 0) o, sin esa columna, `item.value` si hay al menos una fila.
 * - form-checkbox: suma el valor de las opciones marcadas ('na' si marcó "na"; 0 con
 *   `rules.all_options_required` si faltan opciones). Acepta el formato CSV legado.
 * - form-yes-no, form-option, form-select: valor de la opción elegida, 0 si no existe.
 * - form-checkitem: `item.real_value` si está marcado, 0 si no.
 * - cualquier otro tipo: `item.real_value`.
 * @param {Object} item Ítem del formulario (type, options, value, real_value, rules).
 * @param {*} answer Valor guardado de la respuesta.
 * @returns {number|'na'}
 */
export function getItemObtainedPoints (item, answer) {
  const multipleAnswerItems = new Set(['form-checkbox'])
  const multipleOptionItems = new Set(['form-yes-no', 'form-option', 'form-select'])
  const notValuableItems = ['label']
  let options
  if (typeof item.options === 'string') {
    try {
      options = JSON.parse(item.options)
    } catch (e) {
      console.error('[getItemObtainedPoints] item.options is not valid JSON', { itemId: item.id, itemType: item.type, options: item.options, error: e.message })
      options = null
    }
  } else {
    options = item.options
  }

  if (item.type === 'upload-edit-picture' && !answer) {
    return 0
  }

  if (item.type === 'form-table') {
    if (options.scored_table) {
      const scoredColumn = options.columns?.findIndex(col => col.total_for_score) ?? -1
      let ans
      try {
        ans = typeof answer === 'string' ? JSON.parse(answer) : answer
      } catch (e) {
        console.error('[getItemObtainedPoints] answer (form-table) is not valid JSON', { itemId: item.id, answer, error: e.message })
        return 0
      }
      // Tabla con columna total_for_score → puntos reales del total calculado (capped a item.value)
      if (ans && scoredColumn !== -1 && Array.isArray(ans.totals)) {
        const raw = ans.totals[scoredColumn]?.value ?? 0
        const value = raw > item.value ? item.value : raw
        return value >= 0 ? value : 0
      }
      // Tabla sin columna total_for_score → puntaje completo si tiene al menos una fila
      if (ans && Array.isArray(ans.table) && ans.table.length > 0) {
        return item.value ?? 0
      }
      return 0
    }
    return 0
  }

  if (['na', 'NA', '"na"', '"NA"'].includes(answer) || notValuableItems.includes(item.type)) return 'na'

  if (multipleAnswerItems.has(item.type)) {
    let points = 0

    let ans
    try {
      ans = typeof answer === 'string' ? JSON.parse(answer) : answer
    } catch (e) {
      // Legacy: answer stored as comma-separated string instead of JSON array
      console.error('[getItemObtainedPoints] answer (form-checkbox) is not valid JSON, falling back to CSV split', { itemId: item.id, answer, error: e.message })
      ans = typeof answer === 'string' ? answer.split(',').map(s => s.trim()) : answer
    }

    if (item.rules && item.rules.all_options_required && ans.length < options.length) {
      return 0
    }

    if (ans.includes('na') || ans.includes('NA')) {
      points = 'na'
    } else {
      options.forEach(option => {
        if (ans.includes(option.option)) {
          points += Number.parseInt(option.value)
        }
      })
    }

    return points
  }

  if (multipleOptionItems.has(item.type)) {
    const ans = typeof answer === 'string' && answer[0] === '"' ? JSON.parse(answer) : answer
    return options.find(option => option.option === ans) !== undefined
      ? Number.parseInt(options.find(option => option.option === ans).value)
      : 0
  }

  if (item.type === 'form-checkitem') {
    return normalizeCheckitemValue(answer) === true ? item.real_value : 0
  }

  return item.real_value
}

/**
 * Puntos máximos que puede dar un ítem, según su tipo:
 * - respuesta 'na' o 'NA': 0.
 * - form-checkbox: suma de los valores de todas sus opciones.
 * - form-yes-no, form-option, form-select: el valor más alto entre sus opciones (0 si no tiene).
 * - form-table con `options.scored_table`: `item.value`; tabla sin puntaje y label: 0.
 * - cualquier otro tipo: `item.value`.
 * Los getters de la store le pasan como `answer` el arreglo de puntos obtenidos, así que 'na' nunca
 * llega por esa vía: el NA se descuenta aparte, por grupo.
 * @param {Object} item Ítem del formulario (type, options, value).
 * @param {*} answer Respuesta del ítem.
 * @returns {number}
 */
export function getItemPossiblePoints (item, answer) {
  const multipleAnswerItems = new Set(['form-checkbox'])
  const multipleOptionItems = new Set(['form-yes-no', 'form-option', 'form-select'])
  const notValuableItems = ['label', 'form-table']

  if (answer === 'na' || answer === 'NA') return 0

  let options
  try {
    options = typeof item.options === 'string' ? JSON.parse(item.options) : item.options
  } catch (e) {
    console.error('[getItemPossiblePoints] item.options is not valid JSON', { itemId: item.id, itemType: item.type, options: item.options, error: e.message })
    options = null
  }

  if (!options || !Array.isArray(options) || options.length === 0) {
    if (multipleAnswerItems.has(item.type) || multipleOptionItems.has(item.type)) {
      return 0
    }
  }

  if (multipleAnswerItems.has(item.type)) {
    let points = 0

    options.forEach(option => { points += Number.parseInt(option.value) })

    return points
  }

  if (multipleOptionItems.has(item.type)) {
    let max_points = Number.parseInt(options[0].value)

    options.forEach(option => {
      if (Number.parseInt(option.value) > max_points) {
        max_points = Number.parseInt(option.value)
      }
    })

    return max_points
  }

  if (item.type === 'form-table') {
    if (options.scored_table) {
      return item.value
    }
  }

  if (notValuableItems.includes(item.type)) {
    return 0
  }

  return item.value
}

/**
 * Puntos que obtiene un ítem según la respuesta guardada (el objeto de `currentAnswersByItem`), en este orden:
 * - respuesta con `ia_result` definido: 1 si es verdadero, 0 si no.
 * - ítem con `ai_reference_image` y respuesta con valor (no null): su `current_points`, o 0 si no lo tiene.
 * - cualquier otro caso: `getItemObtainedPoints` con el valor de la respuesta; 0 si no hay respuesta.
 * El orden de las condiciones importa: `item.ai_reference_image` solo se lee si la respuesta no trae `ia_result`.
 * @param {Object} item Ítem del formulario.
 * @param {Object|undefined} answer Respuesta del ítem (`{ value, ia_result, current_points }`) o undefined.
 * @returns {number|'na'}
 */
export function getAnsweredItemObtainedPoints (item, answer) {
  if (answer && answer.ia_result !== undefined) {
    return answer.ia_result ? 1 : 0
  }

  if (item.ai_reference_image && answer !== undefined && answer.value !== null) {
    return answer.current_points !== undefined ? answer.current_points : 0
  }

  return answer ? getItemObtainedPoints(item, answer.value) : 0
}

/**
 * Ids de los ítems que tienen reglas de lógica (`logic_properties` con al menos una regla).
 * @param {Array<Object>} items Ítems del formulario.
 * @returns {Set<number>}
 */
export function getLogicPropertyItemIds (items) {
  const ids = new Set()
  items.forEach(item => {
    if (item.logic_properties && item.logic_properties.length > 0) ids.add(item.id)
  })
  return ids
}

/**
 * Ids de los ítems cuya respuesta actual coincide con al menos una de sus reglas de lógica, es decir,
 * la respuesta lleva a un grupo destino. Los ítems cuya respuesta no lleva a ningún grupo se tratan como
 * preguntas normales con puntaje, para que el formulario pueda marcarse como reprobado.
 * La comparación es por texto: la regla `depends_on_value` '1' coincide con 1 punto. Sin puntos (el
 * ítem no está en la lista) o con 'na', no rutea.
 * @param {Array<Object>} items Ítems del formulario.
 * @param {Array<{ id: number, obtained_points: number|string }>} obtainedPointsByItem
 * @returns {Set<number>}
 */
export function getLogicItemsRoutingToVisibleGroup (items, obtainedPointsByItem) {
  const obtainedMap = _.keyBy(obtainedPointsByItem, 'id')
  const ids = new Set()
  items.forEach(item => {
    if (!item.logic_properties || item.logic_properties.length === 0) return
    const points = obtainedMap[item.id]?.obtained_points
    if (points === undefined || points === 'na') return
    const routes = item.logic_properties.some(rule =>
      String(rule.depends_on_value) === String(points)
    )
    if (routes) ids.add(item.id)
  })
  return ids
}

/**
 * Normaliza los puntos de un ítem con lógica: si tiene reglas, no es 'na' y su respuesta rutea a un grupo
 * visible, vale 1 si tiene respuesta y 0 si no. Cualquier otro ítem se devuelve tal cual (mismo objeto).
 * Recibe funciones por id y no conjuntos ya calculados: cada una solo se llama cuando la regla la necesita
 * (por ejemplo, `routesToVisibleGroup` no se llama para un ítem sin lógica).
 * @param {{ id: number, obtained_points: number|string }} item Elemento de `obtainedPointsByItem`.
 * @param {Object} checks
 * @param {Function} checks.isLogicItem `(itemId) => boolean`: el ítem tiene reglas de lógica.
 * @param {Function} checks.routesToVisibleGroup `(itemId) => boolean`: su respuesta lleva a un grupo visible.
 * @param {Function} checks.hasAnswer `(itemId) => boolean`: el ítem tiene respuesta.
 * @returns {{ id: number, obtained_points: number|string }}
 */
export function normalizeItemObtainedPoints (item, { isLogicItem, routesToVisibleGroup, hasAnswer }) {
  if (!isLogicItem(item.id)) return item
  if (item.obtained_points === 'na') return item
  if (!routesToVisibleGroup(item.id)) return item
  return { ...item, obtained_points: hasAnswer(item.id) ? 1 : 0 }
}

/**
 * Puntos obtenidos por grupo: suma, por `group_id`, los puntos de cada ítem ya normalizado, sin contar los 'na'.
 * @param {Array<{ id: number, group_id: number, obtained_points: number|string }>} obtainedPointsByItem
 * @param {Function} normalize `(item) => item`: normaliza los puntos de un ítem con lógica.
 * @returns {Object<string, number>} Puntos por `group_id`.
 */
export function getObtainedPointsByGroup (obtainedPointsByItem, normalize) {
  return _.chain(obtainedPointsByItem)
    .map(item => normalize(item))
    .groupBy('group_id')
    .mapValues(group => sumKeyWhereNotEqual(group, 'obtained_points', 'na'))
    .value()
}

/**
 * Puntos que no aplican por grupo: suma los puntos posibles de los ítems 'na' del grupo (1 si el ítem tiene
 * lógica). Se descuentan de los posibles del grupo al calcular el puntaje.
 * `getPossiblePointsByItem` es una función y no el arreglo: solo se llama si el grupo tiene un ítem 'na' sin
 * lógica. Si el ítem no está en ese arreglo, lanza TypeError.
 * @param {Array<{ id: number, group_id: number, obtained_points: number|string }>} obtainedPointsByItem
 * @param {Function} normalize `(item) => item`: normaliza los puntos de un ítem con lógica.
 * @param {Function} isLogicItem `(itemId) => boolean`: el ítem tiene reglas de lógica.
 * @param {Function} getPossiblePointsByItem `() => Array<{ id: number, possible_points: number }>`
 * @returns {Object<string, number>} Puntos que no aplican por `group_id`.
 */
export function getNAPointsByGroup (obtainedPointsByItem, normalize, isLogicItem, getPossiblePointsByItem) {
  return _.chain(obtainedPointsByItem)
    .map(item => normalize(item))
    .groupBy('group_id')
    .mapValues(group => {
      const naItems = group.filter(item => item.obtained_points === 'na').map(item => {
        if (isLogicItem(item.id)) return { possible_points: 1 }
        return { possible_points: getPossiblePointsByItem().find(_item => _item.id === item.id).possible_points }
      })

      return sumKey(naItems, 'possible_points')
    })
    .value()
}

/**
 * Puntos posibles por grupo: suma, por `group_id`, los puntos posibles de cada ítem. Un ítem con lógica cuya
 * respuesta rutea a un grupo visible vale 1.
 * @param {Array<{ id: number, group_id: number, possible_points: number }>} possiblePointsByItem
 * @param {Function} isLogicItem `(itemId) => boolean`: el ítem tiene reglas de lógica.
 * @param {Function} routesToVisibleGroup `(itemId) => boolean`: su respuesta lleva a un grupo visible.
 * @returns {Object<string, number>} Puntos posibles por `group_id`.
 */
export function getPossiblePointsByGroup (possiblePointsByItem, isLogicItem, routesToVisibleGroup) {
  return _.chain(possiblePointsByItem)
    .map(item => {
      if (!isLogicItem(item.id)) return item
      if (!routesToVisibleGroup(item.id)) return item
      return { ...item, possible_points: 1 }
    })
    .groupBy('group_id')
    .mapValues(group => {
      return sumKey(group, 'possible_points')
    })
    .value()
}

/**
 * Puntaje de una ocurrencia cerrada: el que guardó el servidor. Si es número se formatea con 2 decimales
 * (string); cualquier otro tipo (string, null, undefined) se devuelve tal cual.
 * @param {*} storedScore `score` guardado en la ocurrencia.
 * @returns {string|*}
 */
export function getClosedOccurrenceScore (storedScore) {
  return typeof storedScore === 'number' ? storedScore.toFixed(2) : storedScore
}

/**
 * Puntaje total de una ocurrencia abierta, sobre los grupos visibles. Según el caso devuelve:
 * - obtenidos 0 y posibles 0, formulario solo de tablas y labels (con al menos un ítem): el número 100.
 * - obtenidos 0 y posibles 0 en cualquier otro caso: el número 0.
 * - posibles 0 con obtenidos distintos de 0: el número 0.
 * - cualquier otro caso: el porcentaje como string con 2 decimales (puede ser "NaN").
 * Los posibles de cada grupo ya descuentan los que no aplican.
 * @param {Object} data
 * @param {Object<string, *>} data.formGroups Grupos visibles, por `group_id` (solo se usan sus llaves).
 * @param {Object<string, number>} data.obtainedByGroup
 * @param {Object<string, number>} data.possibleByGroup
 * @param {Object<string, number>} data.naByGroup
 * @param {Array<Object>} data.items Ítems del formulario (solo se leen en el caso 0 de 0).
 * @returns {string|number}
 */
export function getOccurrenceScore ({ formGroups, obtainedByGroup, possibleByGroup, naByGroup, items }) {
  const obtainedPoints = sumArray(
    Object.keys(formGroups).map((groupId) => obtainedByGroup[groupId] || 0)
  )

  const possiblePoints = sumArray(
    Object.keys(formGroups).map((groupId) => (possibleByGroup[groupId] || 0) - (naByGroup[groupId] || 0))
  )

  if (obtainedPoints === 0 && possiblePoints === 0) {
    const allTables = items.length > 0 && items.every(item => item.type === 'form-table' || item.type === 'label')
    return allTables ? 100 : 0
  }

  return possiblePoints !== 0 ? ((obtainedPoints / possiblePoints) * 100).toFixed(2) : 0
}

/**
 * Resultado de la autoevaluación (formularios 1126 y 1134): cuenta, por estándar (`food_security`,
 * `local_standards`, `brand_standards`), los ítems con 1, 2 y 3 puntos y de ahí saca el resultado de cada
 * estándar (`standard`, `marginal` o `low`) y el final, que es el peor de los tres.
 * Supone que cada ítem ya trae `obtained_points` escrito por el getter de la store. Lee `item.priority` primero
 * y `item.obtained_points` solo si la prioridad es de un estándar: leerlo en todos los ítems cambiaría de qué
 * depende el getter. No escribe en los ítems.
 * @param {Array<Object>} items Ítems del formulario con `priority` y `obtained_points`.
 * @returns {{ food_security: string, local_standards: string, brand_standards: string, final: string }}
 */
export function getSelfEvaluationResult (items) {
  const food_security = { level_one: 0, level_two: 0, level_three: 0, result: '' }
  const local_standards = { level_one: 0, level_two: 0, level_three: 0, result: '' }
  const brand_standards = { level_one: 0, level_two: 0, level_three: 0, result: '' }

  items.forEach(item => {
    if (item.priority === 'food_security' && item.obtained_points === 1) food_security.level_one++
    if (item.priority === 'food_security' && item.obtained_points === 2) food_security.level_two++
    if (item.priority === 'food_security' && item.obtained_points === 3) food_security.level_three++

    if (item.priority === 'local_standards' && item.obtained_points === 1) local_standards.level_one++
    if (item.priority === 'local_standards' && item.obtained_points === 2) local_standards.level_two++
    if (item.priority === 'local_standards' && item.obtained_points === 3) local_standards.level_three++

    if (item.priority === 'brand_standards' && item.obtained_points === 1) brand_standards.level_one++
    if (item.priority === 'brand_standards' && item.obtained_points === 2) brand_standards.level_two++
    if (item.priority === 'brand_standards' && item.obtained_points === 3) brand_standards.level_three++
  })

  // food_security rules
  if (food_security.level_one >= 0 && food_security.level_one <= 9) food_security.result = 'standard'
  if (food_security.level_three >= 1 || food_security.level_one >= 10) food_security.result = 'low'

  // local_standards rules
  if (local_standards.level_one >= 0 && local_standards.level_one <= 4) local_standards.result = 'standard'
  if ((local_standards.level_two >= 1 && local_standards.level_two <= 2) || local_standards.level_one >= 5) {
    local_standards.result = 'marginal'
  }
  if (local_standards.level_three >= 1 || local_standards.level_two >= 3) local_standards.result = 'low'

  // brand_standards rules
  if (brand_standards.level_two <= 1 || (brand_standards.level_one >= 1 && brand_standards.level_one <= 9)) {
    brand_standards.result = 'standard'
  }
  if ((brand_standards.level_two >= 2 && brand_standards.level_two <= 3)
    || (brand_standards.level_one >= 10 && brand_standards.level_one <= 14)) {
    brand_standards.result = 'marginal'
  }
  if (brand_standards.level_three >= 1 || brand_standards.level_two >= 4 || brand_standards.level_three >= 15) {
    brand_standards.result = 'low'
  }

  const resultsByStandard = Object.values({ food_security, local_standards, brand_standards }).map(result => result.result)
  let final = ''

  if (resultsByStandard.indexOf('standard') > -1) final = 'standard'
  if (resultsByStandard.indexOf('marginal') > -1) final = 'marginal'
  if (resultsByStandard.indexOf('low') > -1) final = 'low'

  return {
    food_security: food_security.result,
    local_standards: local_standards.result,
    brand_standards: brand_standards.result,
    final: final
  }
}

/**
 * Suma, de las tablas con una columna `total_for_difference`, el valor de esa columna en el total de la
 * respuesta (`parseFloat`). Ignora las tablas sin respuesta, sin esa columna o respondidas con 'na'. La
 * respuesta puede ser un objeto o un string JSON. Una tabla sin `options.columns` lanza TypeError.
 * `getAnswersByItem` es una función y no el objeto: solo se llama si el formulario tiene una tabla.
 * Empieza en 0: es la primera parte de la suma de propiedades extra (`getExtraPropertiesAnswerSum` la continúa).
 * @param {Array<Object>} items Ítems del formulario.
 * @param {Function} getAnswersByItem `() => Object<string, { value: * }>`: respuestas por id de ítem.
 * @returns {number}
 */
export function getTablesDifferenceSum (items, getAnswersByItem) {
  let sum = 0
  items.forEach(item => {
    const answer = item.type === 'form-table' ? getAnswersByItem()[item.id] : undefined
    if (answer) {
      const itemOptions = typeof item.options === 'string' ? JSON.parse(item.options) : item.options
      const usedForTotalIndex = itemOptions.columns.findIndex(column => column.total_for_difference)
      if (usedForTotalIndex !== -1 && answer.value !== 'na') {
        sum += Number.parseFloat(
          (typeof answer.value === 'string'
            ? JSON.parse(answer.value)
            : answer.value
          ).totals[usedForTotalIndex].value
        )
      }
    }
  })
  return sum
}

/**
 * Continúa la suma de propiedades extra: a `startingSum` le suma, una por una, el valor absoluto (`parseFloat`)
 * de la respuesta de cada propiedad con `sum_for_result` de los ítems con `extra_properties`.
 * Recibe la suma ya acumulada en lugar de empezar en 0: así la suma es corrida y el último decimal
 * cambia respecto de sumar por separado (0.1 + 0.2 + 0.3 da 0.6000000000000001, pero 0.1 + (0.2 + 0.3) da 0.6).
 * `getAnswersByItem` es una función y no el objeto: solo se llama si hay una propiedad con `sum_for_result`.
 * No suma nada si no hay respuestas o si no son un objeto plano. Un ítem con respuesta que no trae la llave de
 * la propiedad lanza TypeError.
 * @param {Array<Object>} items Ítems del formulario.
 * @param {Function} getAnswersByItem `() => Object<string, { extra_properties: Array<{ key: string, value: * }> }>`
 * @param {number} [startingSum] Suma ya acumulada (por ejemplo, `getTablesDifferenceSum`).
 * @returns {number}
 */
export function getExtraPropertiesAnswerSum (items, getAnswersByItem, startingSum = 0) {
  let sum = startingSum
  items.filter(item => item.extra_properties && item.extra_properties.length > 0).forEach(item => {
    item.extra_properties.forEach(property => {
      if (property.sum_for_result) {
        const answersByItem = getAnswersByItem()
        if (Object.keys(answersByItem).length !== 0 && answersByItem.constructor === Object) {
          const itemAnswer = answersByItem[item.id]
          const val = itemAnswer && itemAnswer.extra_properties && itemAnswer.extra_properties.length > 0
            ? itemAnswer.extra_properties.find(answer => answer.key === property.key).value
            : 0
          sum += Number.parseFloat(Math.abs(val))
        }
      }
    })
  })
  return sum
}

/**
 * Resultado de Cemex: cuenta los ítems respondidos "no" según el sufijo del nombre (`..._(AA)`, `(A)`, `(B)`,
 * `(C)`) y devuelve 'SUSPENDIDO' (AA ≥ 1, A ≥ 4, B ≥ 6 o C ≥ 6), 'CONDICIONADO' (A ≥ 2, B ≥ 4 o C ≥ 5) o
 * 'APROBADO'. Solo recorre los grupos con ítems.
 * No ordena nada: `getGroupItems(group)` devuelve los ítems del grupo en el orden en que se recorren (el componente
 * los ordena en sitio ahí) y se llama solo para grupos con ítems; `getAnswers()` se llama por ítem. Una respuesta
 * cuyo `value` no es texto lanza TypeError: lo atrapa quien llama.
 * @param {Array<Object>} groups Grupos del formulario, ya ordenados (`items` y `name` de cada ítem).
 * @param {Function} getAnswers `() => Array<{ item_id: number, value: string }>`
 * @param {Function} getGroupItems `(group) => Array<Object>`
 * @returns {'SUSPENDIDO'|'CONDICIONADO'|'APROBADO'}
 */
export function getCemexScoreResult (groups, getAnswers, getGroupItems) {
  let countAA = 0
  let countA = 0
  let countB = 0
  let countC = 0

  groups.forEach(group => {
    if (group.items.length > 0) {
      getGroupItems(group).forEach(item => {
        const answer = getAnswers().find(ans => ans.item_id === item.id)
        const parts = item.name.split('_')
        const suffix = parts[parts.length - 1]

        if (answer && answer.value.toLowerCase() === 'no') {
          if (suffix === '(AA)') countAA++
          else if (suffix === '(A)') countA++
          else if (suffix === '(B)') countB++
          else if (suffix === '(C)') countC++
        }
      })
    }
  })

  if (countAA >= 1 || countA >= 4 || countB >= 6 || countC >= 6) return 'SUSPENDIDO'
  else if (countA >= 2 || countB >= 4 || countC >= 5) return 'CONDICIONADO'
  else return 'APROBADO'
}

/**
 * Nivel de exposición de Mifel según el puntaje total: 90 o más 'Bajo', 80 o más 'Medio', menos de 80 'Alto'.
 * @param {number} puntajeTotal
 * @returns {'Bajo'|'Medio'|'Alto'}
 */
function getNivelExposicion (puntajeTotal) {
  if (puntajeTotal >= 90) return 'Bajo'
  else if (puntajeTotal >= 80) return 'Medio'
  else return 'Alto'
}

/**
 * Resultado de Mifel: calificación ponderada por el porcentaje de ítems "efectivo" o "yes" en los grupos cuyo
 * nombre contiene "alto" (o es "hola"; vale 70 %) y en los que contiene "medio" (o es "adios"; vale 30 %).
 * Las respuestas vacías y "na" no cuentan, ni los ítems sin grupo o sin nombre de grupo. Un nombre que contiene
 * "alto" y "medio" cuenta como alto.
 * @param {Array<Object>|undefined} items Ítems del formulario (con `group.name`); sin ítems todo vale 0.
 * @param {Object<string, { value: * }>} answersByItem Respuesta actual de cada ítem, por id.
 * @returns {{ puntajeTotal: number, nivelExposicion: string, puntajeAlto: number, puntajeMedio: number }}
 */
export function getMifelScoreResult (items, answersByItem) {
  let altoEfectivos = 0
  let altoTotal = 0
  let medioEfectivos = 0
  let medioTotal = 0

  if (items) {
    items.forEach(item => {
      if (item.group && item.group.name) {
        const groupName = item.group.name.toLowerCase()
        const currentAnswer = answersByItem[item.id] ? answersByItem[item.id].value : null
        const answerLower = answerToLowerText(currentAnswer)

        if (answerLower && answerLower !== 'na' && answerLower !== '"na"') {
          if (groupName.includes('alto') || groupName === 'hola') {
            altoTotal++
            if (['efectivo', 'yes'].includes(answerLower)) {
              altoEfectivos++
            }
          } else if (groupName.includes('medio') || groupName === 'adios') {
            medioTotal++
            if (['efectivo', 'yes'].includes(answerLower)) {
              medioEfectivos++
            }
          }
        }
      }
    })
  }

  const puntajeAlto = altoTotal > 0 ? ((altoEfectivos / altoTotal) * 70) : 0
  const puntajeMedio = medioTotal > 0 ? ((medioEfectivos / medioTotal) * 30) : 0
  const puntajeTotal = puntajeAlto + puntajeMedio

  return {
    puntajeTotal,
    nivelExposicion: getNivelExposicion(puntajeTotal),
    puntajeAlto,
    puntajeMedio
  }
}
